import { useRouter } from 'next/router';

import { useEffect } from 'react';

import { Hub } from 'aws-amplify';

import { useCurrentMerchantAccount } from 'data/merchant/hooks/useCurrentMerchantAccount';
import { useCurrentTraderAccount } from 'data/traders/account/hooks/useCurrentTraderAccount';
import { replayUnidentifiedEvents, useMerchantEvent } from 'utils/analytics';
import { identify, reset } from 'utils/analytics/analytics';
import { isSuccess } from 'utils/remote';

function useTrackPageVisits() {
  const { events } = useRouter();
  const trackEvent = useMerchantEvent();

  useEffect(() => {
    trackEvent('page-visit', {
      page: window.location.pathname,
    });
    // Adding trackEvent to the dependencies will cause the visit event to be tracked again when the user is identified.
    // We don't need to care about that because the event will be replayed anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sendRouteChangeEvent = (newPath: string) => {
      const path = newPath.split('?')[0];
      if (window.location.pathname === path) return;
      trackEvent('page-visit', {
        page: path,
      });
    };

    events.on('beforeHistoryChange', sendRouteChangeEvent);

    return () => {
      events.off('beforeHistoryChange', sendRouteChangeEvent);
    };
  }, [events, trackEvent]);
}

function useTrackAuthEvents() {
  const trackEvent = useMerchantEvent();

  useEffect(() => {
    Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;
      switch (event) {
        case 'signIn':
          trackEvent('sign-in');
          break;
        case 'signUp':
          // signUp event is triggered when a user is created in the backend. See userProfilesAtom.ts
          break;
        case 'signOut':
        case 'oAuthSignOut':
          trackEvent('sign-out');
      }
    });
  }, [trackEvent]);
}

function useIdentifyMerchant() {
  const remote = useCurrentMerchantAccount();
  useEffect(() => {
    if (!isSuccess(remote)) return;
    (async () => {
      await identify({
        user: remote.data,
      });
      await replayUnidentifiedEvents();
    })();
  }, [remote]);
}

function useIdentifyTrader() {
  const remote = useCurrentTraderAccount();
  useEffect(() => {
    if (!isSuccess(remote)) return;
    (async () => {
      await identify({
        user: remote.data,
      });
      await replayUnidentifiedEvents();
    })();
  }, [remote]);
}

export function useSetupMerchantAnalytics() {
  useIdentifyMerchant();
  useTrackAuthEvents();
  useTrackPageVisits();
}

export function useSetupTraderAnalytics() {
  useIdentifyTrader();
  useTrackAuthEvents();
  useTrackPageVisits();
}

export function useResetAnalytics() {
  return reset;
}
